'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';

interface NavItem {
  label: string;
  href: string;
  children?: { label: string; href: string }[];
}

const navItems: NavItem[] = [
  { label: 'Home', href: '/' },
  {
    label: 'Digital Marketing',
    href: '/digital-marketing',
    children: [
      { label: 'AI Digital Marketing', href: '/digital-marketing#ai-marketing' },
      { label: 'SEO & Search Marketing', href: '/digital-marketing#seo' },
      { label: 'Social Media Marketing', href: '/digital-marketing#social' },
      { label: 'PPC & Paid Advertising', href: '/digital-marketing#ppc' },
      { label: 'Content Marketing', href: '/digital-marketing#content' },
      { label: 'Email Marketing & Automation', href: '/digital-marketing#email' },
    ],
  },
  {
    label: 'IT Staffing',
    href: '/it-staffing',
    children: [
      { label: 'Hire in India 🇮🇳', href: '/it-staffing#india' },
      { label: 'Hire in USA 🇺🇸', href: '/it-staffing#usa' },
      { label: 'AI/ML Talent', href: '/it-staffing#ai-talent' },
      { label: 'Contract Staffing', href: '/it-staffing#contract' },
      { label: 'Permanent Placement', href: '/it-staffing#permanent' },
      { label: 'Staff Augmentation', href: '/it-staffing#augmentation' },
    ],
  },
  {
    label: 'Offshore Teams',
    href: '/offshore-teams',
    children: [
      { label: 'Build Dedicated Teams', href: '/offshore-teams#dedicated' },
      { label: 'AI & Technology Services', href: '/offshore-teams#technology' },
      { label: 'Managed Development', href: '/offshore-teams#managed' },
      { label: 'Team Scaling', href: '/offshore-teams#scaling' },
    ],
  },
  { label: 'About Us', href: '/about' },
  { label: 'Contact Us', href: '/contact' },
];

const Header: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [scrolled, setScrolled] = useState(false);
  const pathname = usePathname();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  return (
    <header className={`bbt-header transition-shadow duration-300 ${scrolled ? 'shadow-header' : ''}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-[75px]">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-2 flex-shrink-0">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg font-lato">B</span>
              </div>
              <div>
                <div className="font-bold text-text-main text-lg leading-tight font-lato tracking-tight">BBT GROUP</div>
                <div className="text-xs text-primary font-semibold tracking-widest font-lato">BUILD BETTER TEAMS</div>
              </div>
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {navItems.map((item) => (
              <div key={item.href} className="relative nav-item-dropdown">
                {item.children ? (
                  <>
                    <button
                      className={`bbt-nav-link px-3 py-2 flex items-center gap-1 ${
                        pathname.startsWith(item.href) ? 'text-primary' : ''
                      }`}
                      onMouseEnter={() => setOpenDropdown(item.href)}
                      onMouseLeave={() => setOpenDropdown(null)}
                    >
                      {item.label}
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    <div
                      className="dropdown-menu-bbt"
                      onMouseEnter={() => setOpenDropdown(item.href)}
                      onMouseLeave={() => setOpenDropdown(null)}
                    >
                      {item.children.map((child) => (
                        <Link key={child.href} href={child.href}>
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  </>
                ) : (
                  <Link
                    href={item.href}
                    className={`bbt-nav-link px-3 py-2 block ${
                      pathname === item.href ? 'text-primary' : ''
                    }`}
                  >
                    {item.label}
                  </Link>
                )}
              </div>
            ))}
          </nav>

          {/* CTA Button */}
          <div className="hidden lg:flex items-center gap-3">
            <Link href="/contact" className="bbt-btn-primary text-sm">
              Get Started
            </Link>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            className="lg:hidden p-2 text-text-main"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-border-light shadow-lg">
          <div className="max-w-7xl mx-auto px-4 py-4">
            {navItems.map((item) => (
              <div key={item.href} className="border-b border-gray-100 last:border-0">
                <div
                  className="flex items-center justify-between py-3"
                  onClick={() => setOpenDropdown(openDropdown === item.href ? null : item.href)}
                >
                  <Link
                    href={item.href}
                    className={`font-semibold text-sm uppercase tracking-wide font-lato ${
                      pathname === item.href ? 'text-primary' : 'text-text-main'
                    }`}
                  >
                    {item.label}
                  </Link>
                  {item.children && (
                    <svg
                      className={`w-4 h-4 text-gray-400 transition-transform ${
                        openDropdown === item.href ? 'rotate-180' : ''
                      }`}
                      fill="none" stroke="currentColor" viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  )}
                </div>
                {item.children && openDropdown === item.href && (
                  <div className="pl-4 pb-3">
                    {item.children.map((child) => (
                      <Link
                        key={child.href}
                        href={child.href}
                        className="block py-2 text-sm text-gray-600 hover:text-primary font-lato"
                      >
                        {child.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <div className="pt-4">
              <Link href="/contact" className="bbt-btn-primary w-full text-center block">
                Get Started
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
